import { useEffect, useState, type ReactNode } from 'react';
import { Ban, ShieldOff, UserCheck, UserMinus, Users, type LucideIcon } from 'lucide-react';
import { RechargeDialog } from '@/features/channel-quota/ui/recharge-dialog';
import { recordCampaignCut } from '@/features/notifications/model/inbox';
import { toast } from 'sonner';
import { TimePicker } from '@/features/campaigns/ui/time-picker';
import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { DatePicker } from '@/shared/ui/date-picker';
import { Label } from '@/shared/ui/label';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { formatCount } from '../model/format';
import { reviewConsumption } from '../model/review';
import { CHANNEL_COPY, type AccountCapacity, type AudienceSnapshot, type QuotaChannel, type SendMode } from '../model/types';

export function AudienceSummary({
  audience,
  headingId = 'publico-resumo',
  tone = 'plain',
}: {
  audience: AudienceSnapshot;
  headingId?: string;
  /** `kpi` repete o vidro, o ícone e a tipografia do KpiCard, no grid compacto. */
  tone?: 'plain' | 'kpi';
}) {
  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id={headingId} className="text-sm font-medium">
          Público
        </h2>
        <p className="text-xs text-muted-foreground">{audience.audienceName}</p>
      </div>
      <AudienceBreakdown audience={audience} tone={tone} />
    </section>
  );
}

const AUDIENCE_ICONS = {
  total: Users,
  eligible: UserCheck,
  consent: ShieldOff,
  unsubscribed: UserMinus,
  invalid: Ban,
} satisfies Record<string, LucideIcon>;

export function AudienceBreakdown({
  audience,
  tone = 'plain',
  className,
}: {
  audience: AudienceSnapshot;
  /** `kpi` só no SMS: vidro e ícone do KpiCard, em faixa compacta. */
  tone?: 'plain' | 'kpi';
  className?: string;
}) {
  const items: Array<{ label: string; value: number; emphasize: boolean; icon: LucideIcon }> = [
    { label: 'Na lista', value: audience.total, emphasize: false, icon: AUDIENCE_ICONS.total },
    { label: 'Elegíveis', value: audience.eligible, emphasize: audience.eligible === 0, icon: AUDIENCE_ICONS.eligible },
    { label: 'Sem consentimento', value: audience.excludedNoConsent, emphasize: false, icon: AUDIENCE_ICONS.consent },
    { label: 'Descadastrados', value: audience.excludedUnsubscribed, emphasize: false, icon: AUDIENCE_ICONS.unsubscribed },
    { label: 'Inválidos', value: audience.excludedInvalid, emphasize: false, icon: AUDIENCE_ICONS.invalid },
  ];
  if (tone === 'kpi') {
    return (
      <dl className={cn('flex min-w-0 flex-wrap items-center gap-1.5', className)}>
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <Card key={item.label} className="flex items-center gap-1.5 px-2 py-1">
              <span className="grid size-6 shrink-0 place-items-center rounded-lg bg-primary/12 text-primary">
                <Icon className="size-3.5" />
              </span>
              <div className="min-w-0">
                <dt className="text-[11px] leading-tight text-muted-foreground">{item.label}</dt>
                <dd
                  className={cn(
                    'text-sm font-semibold leading-tight tracking-tight tabular-nums',
                    item.emphasize && 'text-destructive',
                  )}
                >
                  {formatCount(item.value)}
                </dd>
              </div>
            </Card>
          );
        })}
      </dl>
    );
  }

  return (
    <dl className={cn('grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5', className)}>
      {items.map((item) => (
        <div key={item.label} className="rounded-xl border border-border/60 px-3 py-2">
          <dt className="text-xs text-muted-foreground">{item.label}</dt>
          <dd
            className={cn(
              'mt-1 text-lg font-semibold tabular-nums',
              item.emphasize && 'text-destructive',
            )}
          >
            {formatCount(item.value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function ConsumptionPanel({
  eligible,
  capacity,
  channel,
  segments = 1,
  passbookId,
  campaignName,
  partialAccepted = false,
  onPartialAccepted,
}: {
  eligible: number;
  capacity: AccountCapacity;
  channel: QuotaChannel;
  /** Unidades por pessoa: partes do SMS. O e-mail é sempre 1. */
  segments?: number;
  passbookId?: string;
  campaignName?: string;
  partialAccepted?: boolean;
  onPartialAccepted?: (value: boolean) => void;
}) {
  const copy = CHANNEL_COPY[channel];
  const review = reviewConsumption(eligible, capacity, {
    unitsPerPerson: segments,
    unit: copy.unit,
    missingUnitsMessage: copy.missingUnits,
  });
  const detail = `${copy.unit} desta campanha, ${
    segments > 1 ? `${formatCount(segments)} por pessoa` : 'um por pessoa elegível'
  }`;
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const showPartial = review.canSendPartial;
  const showRecharge = review.exceedsAvailable || review.quotaEmpty || showPartial;

  useEffect(() => {
    if (!passbookId || !showPartial) return;
    const created = recordCampaignCut({
      passbookId,
      channel,
      campaignName: campaignName?.trim() || 'Campanha sem nome',
      sendPeople: review.sendCount,
      leftOut: review.leftOut,
    });
    const notice = created[0];
    if (!notice) return;
    toast.warning(notice.title, { description: notice.body });
  }, [campaignName, channel, passbookId, review.leftOut, review.sendCount, showPartial]);

  const rows = [
    { label: 'Limite interno da campanha', value: capacity.campaignLimit, exceeded: review.exceedsCampaign, note: '' },
    { label: 'Limite interno da empresa', value: capacity.companyLimit, exceeded: review.exceedsCompany, note: '' },
    { label: 'Disponíveis na conta', value: capacity.available, exceeded: review.exceedsAvailable, note: '' },
    ...(capacity.transactionalReserve > 0
      ? [
          {
            label: 'Reserva para transacionais',
            value: capacity.transactionalReserve,
            exceeded: false,
            note: 'Não entra nesta campanha.',
          },
        ]
      : []),
  ];

  return (
    <section
      aria-labelledby="consumo-titulo"
      className={cn(
        'rounded-2xl border p-4 sm:p-5',
        review.canApprove ? 'border-primary/40 bg-primary/5' : 'border-destructive/40 bg-destructive/5',
      )}
    >
      <h2 id="consumo-titulo" className="text-base font-semibold">
        Antes de aprovar
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Cota deste passbook para {copy.label}. A recarga PIX só aumenta o saldo quando o Bling confirma o pagamento.
      </p>
      <p className="mt-4 text-3xl font-semibold tabular-nums">{formatCount(review.estimated)}</p>
      <p className="text-sm text-muted-foreground">{detail}</p>
      <ul className="mt-4 space-y-2">
        {rows.map((row) => (
          <li key={row.label} className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
            <span className={row.exceeded ? 'font-medium text-destructive' : ''}>
              {row.label}
              {row.note ? <span className="mt-0.5 block text-xs text-muted-foreground">{row.note}</span> : null}
            </span>
            <span className={cn('tabular-nums', row.exceeded && 'font-semibold text-destructive')}>
              {formatCount(row.value)}
            </span>
          </li>
        ))}
      </ul>
      {review.canApprove ? (
        <p className="mt-4 text-sm" role="status">
          A estimativa cabe na cota. Depois deste envio, o disponível ficaria em {formatCount(review.remainingAfter)}.
        </p>
      ) : (
        <ul className="mt-4 space-y-1" role="alert">
          {review.blocks.map((block) => (
            <li key={block} className="text-sm font-medium text-destructive">
              {block}
            </li>
          ))}
        </ul>
      )}
      {showPartial ? (
        <div className="mt-4 space-y-3 rounded-xl border border-border/70 bg-background/60 p-3">
          <p className="text-sm">
            Cabem {formatCount(review.sendCount)} nesta cota. {formatCount(review.leftOut)} ficam de fora se a campanha sair agora.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant={partialAccepted ? 'primary' : 'secondary'} onClick={() => onPartialAccepted?.(!partialAccepted)}>
              {partialAccepted ? 'Envio parcial marcado' : 'Enviar só para quem cabe'}
            </Button>
            {passbookId ? (
              <Button type="button" variant="secondary" onClick={() => setRechargeOpen(true)}>
                Recarregar avulso
              </Button>
            ) : null}
          </div>
          {partialAccepted ? (
            <p className="text-sm" role="status">
              A aprovação reserva {formatCount(review.slotsToReserve)} slots e deixa {formatCount(review.leftOut)} contatos de fora.
            </p>
          ) : null}
        </div>
      ) : null}
      {!showPartial && showRecharge && passbookId ? (
        <div className="mt-4">
          <Button type="button" onClick={() => setRechargeOpen(true)}>
            Recarregar avulso
          </Button>
        </div>
      ) : null}
      {passbookId ? (
        <RechargeDialog
          open={rechargeOpen}
          onOpenChange={setRechargeOpen}
          passbookId={passbookId}
          channel={channel}
          channels={[channel]}
        />
      ) : null}
    </section>
  );
}

export function ReviewSummaryCard({ rows, children }: { rows: ReviewSummaryRowData[]; children?: ReactNode }) {
  return (
    <Card className="min-w-0 p-4 sm:p-5">
      <h2 className="text-sm font-medium">Resumo</h2>
      <dl className="mt-3 space-y-2 text-sm">
        {rows.map((row) => (
          <div key={row.label} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <dt className="shrink-0">
              <Badge variant="secondary">{row.label}</Badge>
            </dt>
            <dd className={cn('min-w-0 break-words text-right font-medium', row.valueClassName)}>{row.value}</dd>
          </div>
        ))}
      </dl>
      {children}
    </Card>
  );
}

export interface ReviewSummaryRowData {
  label: string;
  value: string;
  valueClassName?: string;
}

function splitSchedule(value: string): { date: string; time: string } {
  const match = /^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}))?/.exec(value);
  if (!match) return { date: '', time: '' };
  return { date: match[1], time: match[2] ?? '' };
}

export function SendSchedule({
  sendMode,
  onSendMode,
  scheduledAt,
  onScheduledAt,
  idPrefix,
}: {
  sendMode: SendMode;
  onSendMode: (value: SendMode) => void;
  scheduledAt: string;
  onScheduledAt: (value: string) => void;
  idPrefix: string;
}) {
  const { date, time } = splitSchedule(scheduledAt);

  const setDate = (nextDate: string) => {
    if (!nextDate) {
      onScheduledAt('');
      return;
    }
    onScheduledAt(`${nextDate}T${time || '00:00'}`);
  };

  const setTime = (nextTime: string) => {
    if (!date || !nextTime) return;
    onScheduledAt(`${date}T${nextTime}`);
  };

  return (
    <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
      <fieldset className="flex min-w-0 flex-col gap-2">
        <legend className="text-sm font-medium">Quando enviar</legend>
        <SegmentedControl
          value={sendMode}
          onChange={onSendMode}
          options={[
            { value: 'now', label: 'Na aprovação' },
            { value: 'later', label: 'Agendar' },
          ]}
        />
      </fieldset>
      {sendMode === 'later' ? (
        <div className="flex min-w-0 w-full max-w-full flex-wrap items-end gap-3 sm:w-auto">
          <div className="flex min-w-0 w-full flex-col gap-2 sm:w-48">
            <Label htmlFor={`${idPrefix}-data`}>Data</Label>
            <DatePicker id={`${idPrefix}-data`} value={date} onChange={setDate} placeholder="Escolha a data" />
          </div>
          <TimePicker
            id={`${idPrefix}-hora`}
            label="Horário"
            value={time}
            onChange={setTime}
            className="w-full min-w-0 sm:w-52"
          />
        </div>
      ) : null}
    </div>
  );
}
